import React from 'react';
import { Button, Card, Select, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * AudienceRuleEditor - DNF (Disjunctive Normal Form) audience rule editor
 * 
 * value format: [["audience_A", "audience_B"], ["audience_C"]]
 * meaning: (A AND B) OR C
 * 
 * Each "group" = AND (all audiences in that group must match)
 * Multiple groups = OR (any group matching is sufficient)
 */
const AudienceRuleEditor = ({ value, onChange, audienceOptions = [] }) => {
  // Normalize value: null/undefined → [], ensure 2D array
  const groups = Array.isArray(value) ? value : [];

  const triggerChange = (newGroups) => {
    // Filter out empty groups
    const filtered = newGroups.filter(g => Array.isArray(g) && g.length > 0);
    onChange?.(filtered.length > 0 ? filtered : null);
  };

  const addGroup = () => {
    triggerChange([...groups, []]);
    // Force re-render with empty group for selection
    onChange?.([...groups, []]);
  };

  const removeGroup = (index) => {
    const newGroups = groups.filter((_, i) => i !== index);
    triggerChange(newGroups);
  };

  const updateGroup = (index, selectedValues) => {
    const newGroups = [...groups];
    newGroups[index] = selectedValues || [];
    triggerChange(newGroups);
  };

  // Get already-used audiences across all groups (for info only, allow duplicates across groups)
  const getGroupLabel = (index) => {
    if (groups.length <= 1) return 'Nhóm audience';
    return `Nhóm ${index + 1}`;
  };

  return (
    <div>
      {groups.map((group, index) => (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: 8, border: '1px solid #d9d9d9' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 13 }}>
                {getGroupLabel(index)}
                {groups.length > 1 && <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>(AND trong nhóm)</Text>}
              </Text>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeGroup(index)}
              />
            </div>
          }
        >
          <Select
            mode="multiple"
            placeholder="Chọn tập khách hàng (AND trong nhóm)"
            allowClear
            value={group}
            onChange={(val) => updateGroup(index, val)}
            options={audienceOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').normalize('NFC').toLowerCase().includes(input.normalize('NFC').toLowerCase())
            }
            style={{ width: '100%' }}
          />
        </Card>
      ))}

      {groups.length > 1 && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Các nhóm được kết hợp bằng điều kiện <Text strong>HOẶC (OR)</Text>. 
          Trong mỗi nhóm, các audience được kết hợp bằng điều kiện <Text strong>VÀ (AND)</Text>.
        </Text>
      )}

      <Button
        type="dashed"
        onClick={addGroup}
        icon={<PlusCircleOutlined />}
        style={{ width: '100%' }}
      >
        {groups.length === 0 ? 'Thêm nhóm audience' : 'Thêm nhóm OR'}
      </Button>
    </div>
  );
};

export default AudienceRuleEditor;
